import {
  Add,
  AddShoppingCart,
  DeleteOutline,
  InfoOutlined,
  LocalShipping,
  Remove,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Fade,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

// Config & Store
import { amazonColors } from "../../../config/amazonTheme";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { convertUsdToTjs } from "../../../services/exchangeRateService";
import { useCartStore } from "../../../store/useCartStore";
import { Product } from "../../../types/products";
import Price from "../../common/Price";

// Utils
import {
  normalizeBackendPrice,
  normalizeRegularPriceWithContext,
} from "../../../utils/priceUtils";
import { mapSearchProductToCartProduct } from "../../../utils/productMapper";

interface ProductCardProps {
  product: Product;
  retailer: string;
  index?: number;
}

const brand = {
  text: "#0f172a",
  textSecondary: "#64748b",
  border: "#e2e8f0",
  surface: "#ffffff",
  background: "#f8fafc",
  primary: amazonColors.orange,
};

const ProductCard: React.FC<ProductCardProps> = ({ product, retailer }) => {
  const { t: myTranslate } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("query") || "";
  const [imgLoaded, setImgLoaded] = useState(false);

  // Store Hooks
  const { addToCart, updateQuantity, removeFromCart } = useCartStore();
  const inCart = useCartStore((s) => s.isInCart(product.product_id));
  const quantity = useCartStore((s) => s.getItemQuantity(product.product_id));
  // Find the actual cartItemId for this product (may differ from product_id if seller info exists)
  const cartItemId = useCartStore((s) =>
    s.items.find((item) => item.product.product_id === product.product_id)?.cartItemId ?? product.product_id
  );

  // Exchange rate for TJS display
  const { data: exchangeRate } = useExchangeRate();

  // -- Logic: AppleCare Check --
  const isAppleCareSelected = useMemo(() => {
    if (!product.title) return false;
    const title = product.title.toLowerCase();
    return (
      title.includes("with applecare") && !title.includes("without applecare")
    );
  }, [product.title]);

  // -- Logic: Price Normalization --
  const { normalizedPrice, hasPrice } = useMemo(() => {
    const rawPrice = product.price;
    const isValid = rawPrice !== null && rawPrice !== undefined;

    if (!isValid) return { displayPrice: null, hasPrice: false };

    const normalized =
      normalizeRegularPriceWithContext(
        rawPrice,
        normalizeBackendPrice(rawPrice, "list")
      ) ??
      normalizeBackendPrice(rawPrice, "list") ??
      rawPrice;

    return { normalizedPrice: normalized, hasPrice: true };
  }, [product.price]);

  const tjsAmount = useMemo(() => {
    if (!hasPrice || normalizedPrice == null) return null;
    return convertUsdToTjs(normalizedPrice as number, exchangeRate ?? null);
  }, [normalizedPrice, hasPrice, exchangeRate]);

  // -- Handlers --
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    const cartProduct = mapSearchProductToCartProduct(product, retailer);
    addToCart(cartProduct, 1);
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateQuantity(cartItemId, (quantity || 0) + 1);
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if ((quantity || 0) <= 1) {
      removeFromCart(cartItemId);
    } else {
      updateQuantity(cartItemId, (quantity || 0) - 1);
    }
  };

  const handleCardClick = () => {
    navigate(`/product/${product.product_id}?retailer=${retailer}`, {
      state: { fromSearch: { query, retailer } },
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardClick();
    }
  };

  return (
    <Card
      elevation={0}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        cursor: "pointer",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px -10px rgba(0, 0, 0, 0.1)",
          borderColor: alpha(brand.primary, 0.3),
          "& .product-image": {
            transform: "scale(1.05)",
          },
        },
        "&:focus-visible": {
          outline: `2px solid ${brand.text}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* -- Retailer Badge (Floating) -- */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 2,
          bgcolor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(4px)",
          borderRadius: "8px",
          padding: "4px 8px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          border: `1px solid ${brand.border}`,
          display: "flex",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <Box
          component="img"
          src={`/logos/${retailer}-logo.png`}
          alt={retailer}
          sx={{
            height: 16,
            width: "auto",
            objectFit: "contain",
            display: "block",
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      </Box>

      {/* -- Image Area -- */}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "4 / 3",
          maxHeight: 190,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: brand.background,
          p: 2,
          position: "relative",
        }}
      >
        {!imgLoaded && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            sx={{ position: "absolute", inset: 0 }}
          />
        )}
        <Fade in={imgLoaded} timeout={600}>
          <CardMedia
            component="img"
            className="product-image"
            image={product.image || "/placeholder-product.png"}
            alt={product.title}
            onLoad={() => setImgLoaded(true)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
              mixBlendMode: "multiply",
              filter: "contrast(1.02) brightness(1.02)",
              transition: "transform 0.4s ease-out",
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src =
                "/placeholder-product.png";
            }}
          />
        </Fade>
      </Box>

      {/* -- Content Area -- */}
      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          pt: 2,
          px: 2,
          pb: 2,
          "&:last-child": { pb: 2 },
        }}
      >
        {/* Title */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            fontSize: "0.95rem",
            lineHeight: 1.4,
            color: brand.text,
            mb: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            height: "2.8em",
          }}
          title={product.title}
        >
          {product.title || myTranslate("products.productTitleNotAvailable")}
        </Typography>

        {/* Price & Shipping */}
        <Box sx={{ mt: "auto", mb: 2 }}>
          {hasPrice && (
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                variant="h6"
                className="notranslate"
                translate="no"
                sx={{
                  fontWeight: 800,
                  fontSize: "1.125rem",
                  color: brand.text,
                  letterSpacing: "-0.5px",
                }}
              >
                <Price value={normalizedPrice} format="locale" tjsAmount={tjsAmount} />
              </Typography>
            </Stack>
          )}

          {product.ship_price !== undefined && product.ship_price > 0 ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ mt: 0.5 }}
            >
              <LocalShipping
                sx={{ fontSize: 14, color: brand.textSecondary }}
              />
              <Typography
                variant="caption"
                sx={{ color: brand.textSecondary, fontWeight: 500 }}
              >
                +<Price value={product.ship_price} format="locale" tjsAmount={convertUsdToTjs(Number(product.ship_price) || 0, exchangeRate ?? null)} /> shipping
              </Typography>
            </Stack>
          ) : null}
        </Box>

        {/* Warning Alert */}
        {isAppleCareSelected && (
          <Alert
            severity="warning"
            sx={{
              mb: 1.5,
              py: 0,
              px: 1,
              borderRadius: 2,
              fontSize: "0.75rem",
              alignItems: "center",
              "& .MuiAlert-icon": { mr: 1, fontSize: 16 },
            }}
          >
            {myTranslate("products.appleCareNotSupported")}
          </Alert>
        )}

        {/* -- Action Button -- */}
        {!hasPrice ? (
          <Button
            variant="outlined"
            fullWidth
            onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
            disableElevation
            startIcon={<InfoOutlined sx={{ fontSize: 18 }} />}
            sx={{
              color: brand.text,
              borderColor: brand.border,
              fontWeight: 700,
              fontSize: "0.875rem",
              textTransform: "none",
              borderRadius: "50px",
              height: 40,
              transition: "all 0.2s",
              "&:hover": {
                borderColor: alpha(brand.primary, 0.5),
                bgcolor: alpha(brand.primary, 0.04),
                transform: "scale(1.02)",
              },
            }}
          >
            {myTranslate("products.readMore")}
          </Button>
        ) : !inCart ? (
          <Button
            variant="contained"
            fullWidth
            onClick={handleAddToCart}
            disabled={isAppleCareSelected}
            disableElevation
            startIcon={<AddShoppingCart sx={{ fontSize: 18 }} />}
            sx={{
              bgcolor: amazonColors.yellow,
              color: "#0f172a",
              fontWeight: 700,
              fontSize: "0.875rem",
              textTransform: "none",
              borderRadius: "50px",
              height: 40,
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: amazonColors.yellowHover,
                transform: "scale(1.02)",
                boxShadow: "0 4px 12px rgba(245, 166, 35, 0.3)",
              },
              "&:disabled": {
                bgcolor: "#f1f5f9",
                color: "#94a3b8",
              },
            }}
          >
            {myTranslate("products.addToCart")}
          </Button>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              height: 40,
              borderRadius: "50px",
              bgcolor: "#fff",
              border: `1px solid ${brand.border}`,
              padding: "2px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              size="small"
              onClick={handleDecrement}
              sx={{
                width: 34,
                height: 34,
                color: (quantity || 0) <= 1 ? "#ef4444" : brand.text,
                bgcolor: (quantity || 0) <= 1 ? "#fef2f2" : "#f8fafc",
                "&:hover": {
                  bgcolor: (quantity || 0) <= 1 ? "#fee2e2" : "#f1f5f9",
                },
              }}
            >
              {(quantity || 0) <= 1 ? (
                <DeleteOutline fontSize="small" />
              ) : (
                <Remove fontSize="small" />
              )}
            </IconButton>

            <Typography
              variant="body2"
              sx={{ fontWeight: 700, minWidth: 20, textAlign: "center" }}
            >
              {quantity}
            </Typography>

            <IconButton
              size="small"
              onClick={handleIncrement}
              sx={{
                width: 34,
                height: 34,
                bgcolor: "#f0f9ff",
                color: amazonColors.text.secondary,
                "&:hover": { bgcolor: "#e0f2fe" },
              }}
            >
              <Add fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(ProductCard);
